import { useMemo } from 'react'

import { CustomizableDashboard, ModalContext } from '@domain-group/suit-theme'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { digObject } from '@domain-group/javascript-utils'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import useCurrentUser from '@hooks/useCurrentUser'
import useUser from '@hooks/useUser'

import EditApiLogModal from '@modals/EditApiLogModal'
import ManageEntityQuickLinksModal from '@modals/ManageEntityQuickLinksModal'

import { ApiLogModel, UserModel } from '@models/types'

import Content from '../DashboardModules/Content'
import ContentSidebar from '../DashboardModules/ContentSidebar'

import PageLayout from '../../../../components/PageLayout'

import { dashboardDefaultLayout, generateAvailableModules } from '../../utils/customizableDashboard'

const defaultState = {
  editingDashboard: false,
  showEditApiLogModal: false,
  showManageEntityQuickLinksModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void
type UseUserPayload = ReturnType<typeof useUser>

const callbacks = (
  component: keyof typeof componentCallbacks,
  state: typeof defaultState,
  setState: SetState,
  userCallbacks: UseUserPayload['callbacks'],
) => {
  const { editingDashboard } = state
  const { saveUserDashboard } = userCallbacks

  const componentCallbacks = {
    CustomizableDashboard: {
      saveDashboard: (dashboardKey: string, activeLayout: object) => saveUserDashboard(dashboardKey, activeLayout),
      toggleEditDashboard: () => setState({ editingDashboard: !editingDashboard }),
    },
    EditApiLogModal: {
      closeModal: () => setState({ showEditApiLogModal: false }),
      updateApiLog: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    ManageEntityQuickLinksModal: {
      closeModal: () => setState({ showManageEntityQuickLinksModal: false }),
      updateEntity: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const PageContent = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    editingDashboard,
    showEditApiLogModal,
    showManageEntityQuickLinksModal,
  } = state

  const currentUserPayload = useCurrentUser()
  const { currentUser } = currentUserPayload
  const { callbacks: userCallbacks, user } = useUser(currentUser)

  const customizedLayout = digObject(user, 'options.custom_dashboards.admin_help_desk')

  const availableModules = useMemo(() => generateAvailableModules(currentUserPayload), [user.id])

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showEditApiLogModal: (payload: { apiLog: ApiLogModel }) => {
        setModalData('EditApiLogModal', payload)
        setState({ showEditApiLogModal: true })
      },
      showManageEntityQuickLinksModal: (payload: { user: UserModel }) => {
        setModalData('ManageEntityQuickLinksModal', payload)
        setState({ showManageEntityQuickLinksModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageLayout activeTabBarItemKey="dashboard">
          <CustomizableDashboard
            // @ts-expect-error TS2322
            availableModules={availableModules}
            callbacks={callbacks('CustomizableDashboard', state, setState, userCallbacks)}
            columns={[
              {
                id: 'main',
                component: <Content />,
                title: 'Main',
              },
              {
                id: 'sidebar',
                component: (
                  // @ts-expect-error TS2739
                  <ContentSidebar
                    callbacks={{ customizeDashboard: () => setState({ editingDashboard: !editingDashboard }) }}
                  />
                ),
                title: 'Sidebar',
              },
            ]}
            customizedLayout={customizedLayout}
            dashboardKey="admin_help_desk"
            dashboardReady={!!user.id}
            defaultLayout={dashboardDefaultLayout(currentUserPayload)}
            editing={editingDashboard}
          />
        </PageLayout>

        <EditApiLogModal
          // @ts-expect-error TS2322
          callbacks={callbacks('EditApiLogModal', state, setState, userCallbacks)}
          showModal={showEditApiLogModal}
        />

        <ManageEntityQuickLinksModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManageEntityQuickLinksModal', state, setState, userCallbacks)}
          showModal={showManageEntityQuickLinksModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default PageContent
